import { Button, Checkbox, Pagination, Popover, Table, Toast } from '@douyinfe/semi-ui'
import React, {
	CSSProperties,
	forwardRef,
	ReactNode,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from 'react'
import { IconExport, IconHandle, IconSetting } from '@douyinfe/semi-icons'
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'
import type { Response } from '@src/utils/request'
import type { TableProps } from '@douyinfe/semi-ui/lib/es/table'
import { usePagination } from '@src/hooks/usePagination'
import { downloadBlob } from '@src/api/common/download'
import { useIntl } from 'react-intl'
import * as dateFns from 'date-fns'

/** Semi 默认 Table 表头估算高度（含 filter icon / sorter 等 buffer） */
const TABLE_HEADER_HEIGHT = 56
/** 极窄空间兜底，避免 scroll.y 变成 0 或负数让表格塌掉 */
const TABLE_MIN_SCROLL_Y = 100
/** 列设置弹层宽度 */
const COLUMN_SETTING_WIDTH = 260
/** 列设置弹层中列项的最大滚动高度 */
const COLUMN_SETTING_LIST_MAX_HEIGHT = 320

/** 列表 API 的标准入参：dto 是查询条件，pageNum/pageSize 是分页 */
export type PageQuery<T = unknown> = {
	dto: T
	pageNum: number
	pageSize: number
}

/** 列表 API 的标准出参：list 是数据列表，current/size/total 供分页器使用 */
export type PageResult<T> = {
	list: T[]
	current: number
	size: number
	total: number
}

/** 导出按钮的 props 类型：调用方把现有 param 透传给后端即可 */
export type ExportApiBody<T = unknown> = PageQuery<T>

/** OneTable 通用行类型：未指定时的兜底（兼容历史使用） */
export type DefaultRow = Record<string, unknown>

export interface OneTableOwnProps<T = DefaultRow> extends TableProps {
	style?: CSSProperties
	/**
	 * 列表查询 API。期望入参 PageQuery<T>，期望出参 Response<PageResult<T>>。
	 * 兼容写法：如果你的 API 还没改泛型，可以包一层 `(body) => getXxxList(body.dto)`。
	 */
	refreshApi?: (body: PageQuery<T>) => Promise<Response<PageResult<T>>>
	/** 批量操作 API（顶部勾选后调） */
	batchApi?: (selectedKeys: React.Key[], rows: T[]) => Promise<void>
	rowKey?: string //每行数据的 key ，默认为 id 字段
	/**
	 * 导出接口：传了就自动展示导出按钮，无需额外的开关 prop。
	 * 函数应返回 Blob 二进制流。
	 */
	exportApi?: (body: ExportApiBody<T>) => Promise<Blob>
	/** 下载文件名（配合 exportApi 使用），默认 'export-yyyyMMddHHmmss.xlsx' */
	exportFilename?: string
	/**
	 * 顶部工具条左侧插槽：传任意 ReactNode（如"新建"按钮），
	 * 渲染在表格上方、靠左；导出按钮固定靠右。
	 * 只要传了就一定展示顶部工具条，无需再配 exportApi。
	 */
	topToolbar?: ReactNode
	/**
	 * 是否开启列显隐筛选：开启后顶部工具条右侧会出现齿轮按钮，
	 * 点击弹出列显隐弹层，支持勾选/取消勾选列显隐、拖拽排序。
	 * 默认 true（绝大多数列表页都需要）；不需要的页面显式传 columnSetting={false}。
	 */
	columnSetting?: boolean
}
// 定义 ref 暴露的方法（TypeScript 友好）
export interface OneTableRef {
	refresh: (newParam?: Record<string, unknown>) => void
	batchExec: () => void
	/** 主动触发导出，复用组件内 param / page 状态 */
	export: () => Promise<void>
}

/** 列表项的内部结构：从 TableProps.columns 抽取出的最小信息 */
interface ColumnSettingItem {
	/** 唯一标识，用于在 Set 中追踪显隐 */
	key: string
	/** 弹层里要显示的列名（字符串） */
	title: string
	/**
	 * 固定方向：'left' / 'right' / undefined
	 * - 固定列在 Semi Table 中会冻结在对应边缘，不受 columnOrder 影响
	 * - 列设置里固定列不可拖拽（draggable=false），且始终在弹层两端
	 */
	fixed: 'left' | 'right' | undefined
}

/**
 * 单个可拖拽的列项
 *  - 左侧 IconHandle 作为拖拽手柄（cursor: grab）
 *  - 右侧 Checkbox 控制显隐
 *  - 拖动时整行半透明 + 跟手位移
 *  - draggable=false 时（固定列）：手柄变灰、cursor: not-allowed，useSortable 禁用
 */
interface SortableColumnRowProps {
	item: ColumnSettingItem
	checked: boolean
	disabled: boolean
	draggable: boolean
	onToggle: (key: string, checked: boolean) => void
}
const SortableColumnRow: React.FC<SortableColumnRowProps> = ({ item, checked, disabled, draggable, onToggle }) => {
	// useSortable 的 disabled 会同时禁用"被拖"和"作为 drop target"，
	// 固定列在 SortableContext 中也变成不可落入的位置，自然不会被打乱
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: item.key,
		disabled: !draggable
	})
	const style: React.CSSProperties = {
		transform: draggable ? CSS.Transform.toString(transform) : undefined,
		transition,
		opacity: isDragging ? 0.5 : 1,
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		padding: '4px 0',
		background: isDragging ? 'var(--semi-color-fill-0)' : undefined,
		borderRadius: isDragging ? 4 : 0
	}
	return (
		<div ref={setNodeRef} style={style}>
			{/* 拖拽手柄：固定列灰显 + cursor: not-allowed，普通列 cursor: grab */}
			<span
				{...(draggable ? attributes : {})}
				{...(draggable ? listeners : {})}
				style={{
					cursor: draggable ? 'grab' : 'not-allowed',
					display: 'inline-flex',
					color: draggable ? 'var(--semi-color-text-2)' : 'var(--semi-color-text-3)',
					opacity: draggable ? 1 : 0.4
				}}
				aria-label={draggable ? 'drag-handle' : 'pinned'}
				title={draggable ? undefined : '已固定列，不可拖动'}
			>
				<IconHandle size="small" />
			</span>
			<Checkbox
				checked={checked}
				disabled={disabled}
				onChange={(e) => onToggle(item.key, e.target.checked)}
				style={{ flex: 1 }}
			>
				{item.title}
			</Checkbox>
		</div>
	)
}

export const OneTable = forwardRef<OneTableRef, OneTableOwnProps<DefaultRow>>(
	// 实际类型由 call site 通过 <OneTable<T> ...> 推断或显式指定；
	// 这里用 DefaultRow 兜底，保证旧调用方不传泛型也能编译
	(
		{
			refreshApi,
			batchApi,
			rowKey = 'id',
			className,
			exportApi,
			exportFilename,
			topToolbar,
			columnSetting = true,
			...restProps
		}: OneTableOwnProps<DefaultRow>,
		ref
	) => {
		const { formatMessage: i18nMsg } = useIntl()
		const tableWrapperRef = useRef<HTMLDivElement>(null)
		const [tableScrollY, setTableScrollY] = useState(TABLE_MIN_SCROLL_Y)
		const page = usePagination(1, 20)
		const [isLoading, setIsLoading] = useState(false)
		const [dataSource, setDataSource] = useState<DefaultRow[]>([])
		const [param, setParam] = useState<Record<string, unknown>>({})
		const [isExporting, setIsExporting] = useState(false)
		const isFirstRender = useRef(true)
		// 列设置：可见 key 集合 + 仅追踪"非固定列"的顺序（固定列始终在两端）
		const [visibleColumnKeys, setVisibleColumnKeys] = useState<Set<string>>(() => new Set())
		const [columnOrder, setColumnOrder] = useState<string[]>([])

		// columns 来自 restProps（透传给 Table），单独取出来做列显隐控制
		// 显隐过滤后的 columns 会通过 Table 的 columns 属性强制覆盖 restProps 里的同名字段
		// 类型用 unknown[] 而非 any[]，下游访问具体字段时仍需收敛（保留 SemI column 的灵活性）
		const rawColumns = ((restProps as { columns?: unknown[] }).columns ?? []) as TableProps['columns']

		/**
		 * 把 rawColumns 预计算 key 一次，下游的 items / 排序 / 过滤都直接复用 key，
		 * 避免重复拼接 `__col_${idx}__` 字符串。
		 */
		const rawColumnsWithKey = useMemo(() => {
			return rawColumns.map((col, idx) => ({
				col,
				key: String(col?.key ?? col?.dataIndex ?? `__col_${idx}__`),
				idx
			}))
		}, [rawColumns])

		/**
		 * 抽取列项：key 已经算好；title 取 col.title 字符串（项目里通常都是 i18n 后的字符串）。
		 * 用 useMemo 减少父组件无关重渲染时的重复计算。
		 * fixed 保留原始 'left' / 'right' 方向（不转 boolean），便于按左右分组
		 */
		const allColumnItems: ColumnSettingItem[] = useMemo(() => {
			return rawColumnsWithKey.map(({ col, key, idx }) => {
				let title = ''
				if (typeof col?.title === 'string') {
					title = col.title
				} else if (col?.title && typeof col.title === 'object' && 'props' in col.title) {
					// 兼容 title 是 i18nMsg 节点的情况（极少数）
					const titleProps = (col.title as { props?: { id?: string } }).props
					if (titleProps?.id) {
						title = i18nMsg({ id: titleProps.id })
					}
				}
				if (!title) {
					title = `${i18nMsg({ id: 'app.system.common.columnSetting.title' })} ${idx + 1}`
				}
				const fixedVal = col?.fixed
				return {
					key,
					title,
					fixed: fixedVal === 'left' || fixedVal === 'right' ? fixedVal : undefined
				}
			})
		}, [rawColumnsWithKey])

		/**
		 * 三段分组：固定列在表格里始终在边缘，所以弹层也固定在两端，
		 * 与表格实际渲染顺序一致，普通列只能在"普通列"组内拖动。
		 *  - leftFixedItems：fixed: 'left'，按 allColumnItems 自然顺序
		 *  - rightFixedItems：fixed: 'right'，按 allColumnItems 自然顺序
		 *  - normalItems：非固定列，按 columnOrder 排序
		 */
		const { leftFixedItems, rightFixedItems, normalItems } = useMemo(() => {
			const left: ColumnSettingItem[] = []
			const right: ColumnSettingItem[] = []
			const normal: ColumnSettingItem[] = []
			for (const c of allColumnItems) {
				if (c.fixed === 'left') left.push(c)
				else if (c.fixed === 'right') right.push(c)
				else normal.push(c)
			}
			// 普通列按 columnOrder 排序
			normal.sort((a, b) => {
				const ai = columnOrder.indexOf(a.key)
				const bi = columnOrder.indexOf(b.key)
				if (ai === -1) return 1
				if (bi === -1) return -1
				return ai - bi
			})
			return { leftFixedItems: left, rightFixedItems: right, normalItems: normal }
		}, [allColumnItems, columnOrder])

		/**
		 * 当 columns 变化时同步两个状态：
		 *  - visibleColumnKeys：新增的列默认可见，已存在的保留用户上次选择
		 *  - columnOrder：仅追踪非固定列的顺序；保留 prev 中仍存在的非固定 key，新出现的非固定 key 追加到末尾
		 */
		useEffect(() => {
			setVisibleColumnKeys((prev) => {
				const next = new Set(prev)
				for (const c of allColumnItems) {
					if (!next.has(c.key)) next.add(c.key)
				}
				return next
			})
			setColumnOrder((prev) => {
				const normalKeys = allColumnItems.filter((c) => !c.fixed).map((c) => c.key)
				const normalSet = new Set(normalKeys)
				const prevSet = new Set(prev)
				const result: string[] = []
				// 保留 prev 中仍是"非固定"且存在的 key（保持用户自定义顺序）
				for (const k of prev) {
					if (normalSet.has(k)) result.push(k)
				}
				// 追加新出现的非固定 key
				for (const k of normalKeys) {
					if (!prevSet.has(k)) result.push(k)
				}
				return result
			})
		}, [allColumnItems])

		/**
		 * 过滤后传给 Table 的列：
		 *   [左固定（按自然顺序）] + [普通（按 columnOrder）] + [右固定（按自然顺序）]
		 * 固定列 Semi 会自动冻结到表格边缘，所以中间普通列的相对顺序就是用户拖拽的结果
		 */
		const filteredColumns = useMemo(() => {
			const keyToCol = new Map<string, NonNullable<TableProps['columns']>[number]>()
			for (const { col, key } of rawColumnsWithKey) {
				keyToCol.set(key, col)
			}
			const out: NonNullable<TableProps['columns']> = []
			for (const c of leftFixedItems) {
				if (visibleColumnKeys.has(c.key)) {
					const col = keyToCol.get(c.key)
					if (col) out.push(col)
				}
			}
			for (const c of normalItems) {
				if (visibleColumnKeys.has(c.key)) {
					const col = keyToCol.get(c.key)
					if (col) out.push(col)
				}
			}
			for (const c of rightFixedItems) {
				if (visibleColumnKeys.has(c.key)) {
					const col = keyToCol.get(c.key)
					if (col) out.push(col)
				}
			}
			return out
		}, [rawColumnsWithKey, leftFixedItems, normalItems, rightFixedItems, visibleColumnKeys])

		/** 切换单列显隐：避免最后一列也被关掉（空表无法挽回） */
		const toggleColumn = (key: string, checked: boolean) => {
			setVisibleColumnKeys((prev) => {
				if (!checked && prev.size === 1 && prev.has(key)) {
					return prev
				}
				const next = new Set(prev)
				if (checked) next.add(key)
				else next.delete(key)
				return next
			})
		}

		/** 全选/全不选：至少保留一列 */
		const allChecked = allColumnItems.length > 0 && allColumnItems.every((c) => visibleColumnKeys.has(c.key))
		const noneChecked = allColumnItems.every((c) => !visibleColumnKeys.has(c.key))
		const someChecked = !allChecked && !noneChecked
		const toggleAll = (checked: boolean) => {
			if (!checked) {
				// 全不选时强制保留第一个（弹层最上方那个）
				const firstKey = leftFixedItems[0]?.key ?? normalItems[0]?.key ?? rightFixedItems[0]?.key
				setVisibleColumnKeys(new Set(firstKey ? [firstKey] : []))
			} else {
				setVisibleColumnKeys(new Set(allColumnItems.map((c) => c.key)))
			}
		}

		/** 重置：恢复全部列可见 + 恢复普通列的初始顺序（固定列无需重排） */
		const resetColumns = () => {
			setVisibleColumnKeys(new Set(allColumnItems.map((c) => c.key)))
			setColumnOrder(allColumnItems.filter((c) => !c.fixed).map((c) => c.key))
		}

		/**
		 * 拖拽结束：调 arrayMove 重排 columnOrder（仅作用于普通列）
		 * 用 PointerSensor 避免与 Checkbox 点击冲突（按下 5px 距离才算拖）
		 */
		const sensors = useSensors(
			useSensor(PointerSensor, {
				activationConstraint: { distance: 5 }
			})
		)
		const handleDragEnd = (event: DragEndEvent) => {
			const { active, over } = event
			if (!over || active.id === over.id) return
			setColumnOrder((prev) => {
				const oldIndex = prev.indexOf(String(active.id))
				const newIndex = prev.indexOf(String(over.id))
				if (oldIndex < 0 || newIndex < 0) return prev
				return arrayMove(prev, oldIndex, newIndex)
			})
		}

		/** 生成默认导出文件名：export-yyyyMMddHHmmss.xlsx */
		const buildDefaultFilename = () => {
			const stamp = dateFns.format(new Date(), 'yyyyMMddHHmmss')
			return exportFilename || `export-${stamp}.xlsx`
		}
		/** 触发导出，复用当前 param / page */
		const handleExport = async () => {
			if (!exportApi) return
			setIsExporting(true)
			const loadingId = Toast.info({
				content: i18nMsg({ id: 'app.common.loading' }),
				duration: 0,
				showClose: false
			})
			try {
				const blob = await exportApi({
					dto: param as Record<string, unknown>,
					pageNum: page.current,
					pageSize: page.pageSize
				})
				downloadBlob(blob, buildDefaultFilename())
				Toast.success(i18nMsg({ id: 'app.common.success.export' }))
			} catch (e) {
				const msg = e instanceof Error ? e.message : i18nMsg({ id: 'app.common.error.export' })
				Toast.error(msg)
			} finally {
				Toast.close(loadingId)
				setIsExporting(false)
			}
		}
		// 对外暴露 refresh 方法
		useImperativeHandle(ref, () => ({
			refresh: (newParam) => {
				setParam(newParam ?? {})
				queryTable(newParam ?? {})
			},
			batchExec: async () => {
				if (batchApi) {
					await batchApi(selectedKeys, dataSource)
				}
			},
			export: handleExport
		}))
		const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])

		const rowSelectionConfig = batchApi
			? {
					getCheckboxProps: (record: DefaultRow) => ({
						id: String((record as { id?: React.Key }).id ?? '')
					}),
					onChange: (selectedRowKeys: React.Key[]) => {
						setSelectedKeys(selectedRowKeys)
					},
					selectedRowKeys: selectedKeys as (string | number)[]
				}
			: undefined
		const handlePageChange = (current: number) => {
			page.setCurrent(current)
		}
		const handlePageSizeChange = (pageSize: number) => {
			page.setPageSize(pageSize)
		}
		// 查询函数：仅在 page 变化时由 useEffect 触发；param 变化需父组件显式调 refresh()
		const queryTable = async (body: Record<string, unknown>) => {
			if (refreshApi) {
				try {
					setIsLoading(true)
					const res = await refreshApi({ dto: body, pageNum: page.current, pageSize: page.pageSize })
					page.updateFromRes(res.data ?? {})
					setDataSource(res.data?.list ?? [])
				} finally {
					setIsLoading(false)
				}
			}
		}
		useEffect(() => {
			if (isFirstRender.current) {
				isFirstRender.current = false
				return
			}
			queryTable(param)
			// 仅在分页变化时重新拉取；param 变化由对外暴露的 refresh() 主动触发
		}, [page.current, page.pageSize])

		/**
		 * 用 ResizeObserver 实时测量表格容器（flex: 1 后的真实高度）
		 * 然后把 (高度 - 表头高度) 作为 Table.scroll.y
		 */
		useEffect(() => {
			const el = tableWrapperRef.current
			if (!el) return
			const observer = new ResizeObserver((entries) => {
				for (const entry of entries) {
					const height = entry.contentRect.height
					setTableScrollY(Math.max(TABLE_MIN_SCROLL_Y, Math.floor(height - TABLE_HEADER_HEIGHT)))
				}
			})
			observer.observe(el)
			return () => observer.disconnect()
		}, [])

		/**
		 * 渲染单行固定列（不可拖）
		 * - 复用一个不可拖的 SortableColumnRow
		 * - 也可改为普通 div，但用同一个组件能保证视觉一致
		 */
		const renderFixedRow = (c: ColumnSettingItem) => (
			<SortableColumnRow
				key={c.key}
				item={c}
				checked={visibleColumnKeys.has(c.key)}
				disabled={!!c.fixed && visibleColumnKeys.has(c.key)}
				draggable={false}
				onToggle={toggleColumn}
			/>
		)

		/** 列设置弹层内容：分三段（左侧固定 / 普通 / 右侧固定），只有普通段可拖 */
		const renderColumnSettingContent = () => {
			const showLeftSection = leftFixedItems.length > 0
			const showNormalSection = normalItems.length > 0
			const showRightSection = rightFixedItems.length > 0
			const dividerStyle: React.CSSProperties = {
				height: 1,
				background: 'var(--semi-color-border)',
				margin: '4px 0'
			}
			return (
				<div style={{ width: COLUMN_SETTING_WIDTH }}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							padding: '4px 8px 8px',
							borderBottom: '1px solid var(--semi-color-border)'
						}}
					>
						<Checkbox checked={allChecked} indeterminate={someChecked} onChange={(e) => toggleAll(e.target.checked)}>
							{i18nMsg({ id: 'app.system.common.columnSetting.checkAll' })}
						</Checkbox>
						<Button type="tertiary" theme="borderless" onClick={resetColumns}>
							{i18nMsg({ id: 'app.system.common.columnSetting.reset' })}
						</Button>
					</div>
					<div
						style={{
							maxHeight: COLUMN_SETTING_LIST_MAX_HEIGHT,
							minHeight: 0,
							overflowY: 'auto',
							overflowX: 'hidden',
							padding: '4px 8px 8px'
						}}
					>
						{/* 1. 左侧固定列：分到 SortableContext 外，不可拖 */}
						{showLeftSection && <>{leftFixedItems.map(renderFixedRow)}</>}

						{/* 2. 普通列：唯一可拖的部分 */}
						{showNormalSection && (
							<>
								{showLeftSection && <div style={dividerStyle} />}
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									onDragEnd={handleDragEnd}
									modifiers={[restrictToVerticalAxis, restrictToParentElement]}
								>
									<SortableContext items={normalItems.map((i) => i.key)} strategy={verticalListSortingStrategy}>
										{normalItems.map((c) => (
											<SortableColumnRow
												key={c.key}
												item={c}
												checked={visibleColumnKeys.has(c.key)}
												disabled={false}
												draggable
												onToggle={toggleColumn}
											/>
										))}
									</SortableContext>
								</DndContext>
							</>
						)}

						{/* 3. 右侧固定列：分到 SortableContext 外，不可拖 */}
						{showRightSection && (
							<>
								<div style={dividerStyle} />
								{rightFixedItems.map(renderFixedRow)}
							</>
						)}
					</div>
				</div>
			)
		}

		return (
			<div
				className={'one-table-root'}
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					minHeight: 0
				}}
			>
				{(topToolbar || exportApi || columnSetting) && (
					<div
						style={{
							flexShrink: 0,
							padding: '8px 20px',
							background: 'var(--semi-color-bg-0)',
							borderBottom: '1px solid var(--semi-color-border)',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: 8
						}}
					>
						<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{topToolbar}</div>
						<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
							{columnSetting && (
								<Popover trigger="click" position="bottomRight" showArrow content={renderColumnSettingContent}>
									<Button
										type="tertiary"
										theme="borderless"
										icon={<IconSetting />}
										aria-label={i18nMsg({ id: 'app.system.common.button.columnSetting' })}
									/>
								</Popover>
							)}
							{exportApi && (
								<Button
									type="tertiary"
									theme="borderless"
									icon={<IconExport />}
									loading={isExporting}
									disabled={isExporting}
									onClick={handleExport}
									aria-label={i18nMsg({ id: 'app.system.common.button.export' })}
								/>
							)}
						</div>
					</div>
				)}
				<div
					ref={tableWrapperRef}
					style={{
						flex: 1,
						minHeight: 0,
						overflow: 'hidden'
					}}
				>
					<Table
						rowKey={rowKey}
						loading={isLoading}
						scroll={{ x: '100%', y: tableScrollY }}
						dataSource={dataSource}
						rowSelection={rowSelectionConfig}
						{...restProps}
						columns={filteredColumns}
						pagination={false}
					/>
				</div>
				<div
					style={{
						flexShrink: 0,
						padding: '8px 20px',
						background: 'var(--semi-color-bg-0)',
						borderTop: '1px solid var(--semi-color-border)',
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center'
					}}
				>
					<Pagination
						currentPage={page.current}
						pageSize={page.pageSize}
						total={page.total}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						showSizeChanger={true}
						pageSizeOpts={[10, 20, 50, 200]}
					/>
				</div>
			</div>
		)
	}
)
export default OneTable
